/**
 * Roster placement card view-model — Phaser-free, no `t()`.
 * Sell axes = Judgment Selling (직감/통찰/유대); grit demoted role floor.
 * Labels/tooltips come from SSOT via HUD glue — this module stays numeric.
 */
import {
  raidCatalog,
  raidGambitRulesFor,
  raidHeroProfileFor,
  resolveInstinct,
  type RaidAxisId,
  type RaidClassId,
  type RaidDisposition,
  type RaidGambitRow,
} from "../../data/raid-catalog";
import {
  engagementStyle,
  resolveDisposition,
  type EngagementStyle,
} from "./raid-brain";
import { legacyToJudgment } from "./judgment/legacy-map";
import {
  JUDGMENT_SELL_TRAITS,
  type JudgmentSellTraitId,
  type JudgmentTraits,
} from "./judgment/traits";
import { poolEntries, isExperimentalRosterPick } from "./raid-roster-rules";

export type RaidRosterGambitChip = {
  /** Condition chip id (before `:arg`), e.g. `bossClose`. */
  condId: string;
  /** Behavior chip id, e.g. `space`. */
  behaviorId: string;
  condKey: string;
  behaviorKey: string;
  raw: RaidGambitRow;
};

/** Combat kit strip for roster cards — values from `balance.classes` only. */
export type RaidRosterCombatSnap = {
  hp: number;
  damage: number;
  attackRange: number;
  speed: number;
  attackCd: number;
};

/** Card bar order: sell 3 + demoted grit. */
export const ROSTER_SELL_BAR_IDS = [
  ...JUDGMENT_SELL_TRAITS,
  "grit",
] as const;

export type RaidRosterSellBarId = (typeof ROSTER_SELL_BAR_IDS)[number];

export type RaidRosterCardSnap = {
  defId: string;
  classId: RaidClassId;
  nameKey: string;
  classKey: string;
  /** Table quirk id → `raid.quirk.<id>` label. */
  quirkId: string;
  quirkKey: string;
  engagement: EngagementStyle;
  engagementKey: string;
  fleeFromAdds: boolean;
  /** Bench / flawed picks outside the elite default 12. */
  experimental: boolean;
  combat: RaidRosterCombatSnap;
  /** Legacy runtime disposition (grit/measure/selfProtect/bond). */
  disposition: RaidDisposition;
  /** Judgment fold — sell sheet values. */
  judgment: JudgmentTraits;
  /** @deprecated use judgment.instinct */
  instinct: number;
  /** Legacy axis ids still present in catalogs. */
  axes: readonly RaidAxisId[];
  /** Sell bars shown on the card (instinct/insight/bond/grit). */
  sellBars: readonly RaidRosterSellBarId[];
  /** Highest among sell traits only (grit never wins "강점"). */
  dominantSell: JudgmentSellTraitId;
  /**
   * Legacy dominant among runtime axes — kept for divergence tests.
   * HUD uses `dominantSell`.
   */
  dominantAxis: RaidAxisId;
  gambits: RaidRosterGambitChip[];
  /** Trait label keys derived from `profiles.classKnobs` (truthy flags + heal pct). */
  traitKeys: string[];
};

function condIdOf(raw: string): string {
  const i = raw.indexOf(":");
  return i >= 0 ? raw.slice(0, i) : raw;
}

function gambitChips(
  defId: string,
  classId: RaidClassId,
): RaidRosterGambitChip[] {
  return raidGambitRulesFor(defId, classId).map((raw) => {
    const condId = condIdOf(raw[0]);
    const behaviorId = raw[1];
    return {
      condId,
      behaviorId,
      condKey: `raid.gambit.cond.${condId}`,
      behaviorKey: `raid.gambit.beh.${behaviorId}`,
      raw,
    };
  });
}

/** Map classKnobs → string keys; values stay in tables, labels in strings. */
function traitKeysFor(classId: RaidClassId): string[] {
  const knobs = raidCatalog.profiles.classKnobs[classId] as Record<
    string,
    boolean | number
  >;
  const out: string[] = [];
  for (const [k, v] of Object.entries(knobs)) {
    if (typeof v === "boolean" && v) out.push(`raid.trait.${k}`);
    else if (typeof v === "number" && k === "healLowestAllyBelowPct") {
      out.push(`raid.trait.${k}`);
    }
  }
  const flee = raidCatalog.balance.classes[classId].fleeFromAdds;
  if (flee) out.push("raid.trait.fleeFromAdds");
  else out.push("raid.trait.standVsAdds");
  return out;
}

function dominantLegacyAxis(
  d: RaidDisposition,
  axes: readonly RaidAxisId[],
): RaidAxisId {
  let best: RaidAxisId = axes[0] ?? "grit";
  let bestV = -1;
  for (const a of axes) {
    const v = d[a] ?? 0;
    if (v > bestV) {
      bestV = v;
      best = a;
    }
  }
  return best;
}

function dominantSellTrait(j: JudgmentTraits): JudgmentSellTraitId {
  let best: JudgmentSellTraitId = JUDGMENT_SELL_TRAITS[0];
  let bestV = -1;
  for (const a of JUDGMENT_SELL_TRAITS) {
    const v = j[a];
    if (v > bestV) {
      bestV = v;
      best = a;
    }
  }
  return best;
}

export function rosterCardFor(defId: string): RaidRosterCardSnap | null {
  const entry = poolEntries().find((p) => p.id === defId);
  if (!entry) return null;
  const classId = entry.classId;
  const kit = raidCatalog.balance.classes[classId];
  const axes = raidCatalog.profiles.axes as RaidAxisId[];
  const disposition = resolveDisposition(defId, classId);
  const instinct = resolveInstinct(defId, classId);
  const judgment = legacyToJudgment({ ...disposition, instinct });
  const engagement = engagementStyle(classId);
  const hero = raidHeroProfileFor(defId);
  const quirkId = hero?.quirkId ?? "default";
  return {
    defId,
    classId,
    nameKey: entry.nameKey,
    classKey: `raid.class.${classId}`,
    quirkId,
    quirkKey: `raid.quirk.${quirkId}`,
    engagement,
    engagementKey: `raid.engage.${engagement}`,
    fleeFromAdds: kit.fleeFromAdds !== false,
    experimental: isExperimentalRosterPick(defId),
    combat: {
      hp: kit.hp,
      damage: kit.damage,
      attackRange: kit.attackRange,
      speed: kit.speed,
      attackCd: kit.attackCd,
    },
    disposition,
    judgment,
    instinct,
    axes,
    sellBars: ROSTER_SELL_BAR_IDS,
    dominantSell: dominantSellTrait(judgment),
    dominantAxis: dominantLegacyAxis(disposition, axes),
    gambits: gambitChips(defId, classId),
    traitKeys: traitKeysFor(classId),
  };
}

export function rosterCardsForPool(): RaidRosterCardSnap[] {
  const out: RaidRosterCardSnap[] = [];
  for (const e of poolEntries()) {
    const c = rosterCardFor(e.id);
    if (c) out.push(c);
  }
  out.sort((a, b) => Number(a.experimental) - Number(b.experimental));
  return out;
}
